import type { ShellHandle } from "@observe-tech/mfe-shell-api";

// The shell is the single source of truth for the selectors (start, end,
// sites, cages). This module is the ONLY writer of those four URL params:
// the URL is a projection of shell state. MFEs read the params on mount and
// must never write them directly.

export type SelectorParams = {
  start: string | null;
  end: string | null;
  sites: number[];
  cages: string[];
};

// URLSearchParams.toString() encodes a space as "+". Consumers that decode with
// decodeURIComponent — tanstack-router's qss, which is what the MFEs parse their
// search with — read that back as a literal "+", so "2026-07-25 23:59:59"
// arrives as "2026-07-25+23:59:59" and tide's parseDateParam truncates it at the
// "+" to a bare date. Percent-encode instead so datetimes survive the hop.
export function toQueryString(params: URLSearchParams): string {
  return params.toString().replace(/\+/g, "%20");
}

export function readSelectors(handle: ShellHandle): SelectorParams {
  const range = handle.dateRange.range.get();
  return {
    start: range.start ?? null,
    end: range.end ?? null,
    sites: handle.sites.selectedSiteIds.get() ?? [],
    cages: handle.sites.selectedCageIds.get() ?? [],
  };
}

// Build the `?start=…&end=…&sites=[…]&cages=[…]` query for the current shell
// state. Empty values are omitted. Returns "" when no selector is set.
export function buildSelectorQuery(handle: ShellHandle): string {
  const { start, end, sites, cages } = readSelectors(handle);
  const qs = new URLSearchParams();
  if (start) qs.set("start", start);
  if (end) qs.set("end", end);
  if (sites.length) qs.set("sites", JSON.stringify(sites));
  if (cages.length) qs.set("cages", JSON.stringify(cages));
  const s = toQueryString(qs);
  return s ? "?" + s : "";
}

// Merge the shell's selector params into an arbitrary destination URL,
// letting params already on `url` win (e.g. a sidebar item's own search).
export function withSelectors(url: string, handle: ShellHandle): string {
  const [path, hash = ""] = url.split("#");
  const [pathname, existing = ""] = path.split("?");
  const merged = new URLSearchParams(
    buildSelectorQuery(handle).replace(/^\?/, ""),
  );
  for (const [k, v] of new URLSearchParams(existing)) merged.set(k, v);
  const s = toQueryString(merged);
  return pathname + (s ? "?" + s : "") + (hash ? "#" + hash : "");
}

// Project the current selector params onto the live URL via replaceState,
// preserving pathname + hash. Idempotent: no write when the search already
// matches, so it never loops with main.tsx's hash mirror.
function projectToUrl(handle: ShellHandle): void {
  const current = window.location.search; // "" or "?a=b"
  // Preserve any non-selector params already present, with selectors taking
  // the shell's value.
  const next = new URLSearchParams(current);
  const { start, end, sites, cages } = readSelectors(handle);
  if (start) next.set("start", start);
  else next.delete("start");
  if (end) next.set("end", end);
  else next.delete("end");
  if (sites.length) next.set("sites", JSON.stringify(sites));
  else next.delete("sites");
  if (cages.length) next.set("cages", JSON.stringify(cages));
  else next.delete("cages");
  const s = toQueryString(next);
  const nextSearch = s ? "?" + s : "";
  if (nextSearch === current) return;
  history.replaceState(
    history.state,
    "",
    window.location.pathname + nextSearch + window.location.hash,
  );
}

let started = false;

export function startUrlSync(handle: ShellHandle): () => void {
  if (started) return () => {};
  started = true;
  const unsubs = [
    handle.dateRange.range.subscribe(() => projectToUrl(handle)),
    handle.sites.selectedSiteIds.subscribe(() => projectToUrl(handle)),
    handle.sites.selectedCageIds.subscribe(() => projectToUrl(handle)),
  ];
  // Project the boot state immediately so a freshly-read deep link is
  // normalized and present for the first MFE mount.
  projectToUrl(handle);
  return () => {
    unsubs.forEach((u) => u());
    started = false;
  };
}
